import { useParams, useSearchParams } from 'react-router'
import { FlowSuccess } from './FlowSuccess'
import { formatMoney, fromMajor, money } from '@/lib/money'
import { FLIGHTS, FLIGHT_CASHBACK, findFlight } from '@/features/explore/travel-data'
import { findVenue, WELLNESS_CASHBACK } from '@/features/explore/wellness-data'
import { BRAND } from '@/brand/brand'

export function SendDoneScreen() {
  return (
    <FlowSuccess
      headline="On its way"
      amount="MX$8,631.61"
      caption="To María Fernanda Ruiz · CLABE ••4412"
      rows={[
        { label: 'You sent', value: '$500.00' },
        { label: 'Our fee · 1.0%', value: '$5.00' },
        { label: 'Rate', value: '1 USD = 17.26 MXN' },
        { label: 'Arrives', value: 'In minutes', emphasis: true },
        { label: 'Reference', value: 'PB-4471-90A2' },
      ]}
      primary={{ label: 'Back to Home', to: '/' }}
      secondary={{ label: 'Send another transfer', to: '/transfers/send' }}
      footnote="We’ll notify you the moment it lands. Nothing else is charged."
    />
  )
}

export function ConvertDoneScreen() {
  return (
    <FlowSuccess
      headline="Converted"
      amount="€4,601.70"
      caption="From your US Dollar account to Euro"
      rows={[
        { label: 'You converted', value: '$5,000.00' },
        { label: 'Rate', value: '1 USD = 0.9203 EUR' },
        { label: 'Spread · 0.6%', value: '$30.00' },
        { label: 'Conversion fee', value: 'None', emphasis: true },
      ]}
      primary={{ label: 'See my accounts', to: '/accounts' }}
      secondary={{ label: 'Convert again', to: '/convert' }}
      footnote="The euros are available right now, including on your card."
    />
  )
}

export function AddMoneyDoneScreen() {
  return (
    <FlowSuccess
      headline="Money added"
      amount="$500.00"
      caption="Bank transfer from Chase ••7781"
      rows={[
        { label: 'Amount', value: '$500.00' },
        { label: 'Fee', value: 'Free', emphasis: true },
        { label: 'Lands in', value: 'US Dollar account' },
        { label: 'Available', value: 'Same day' },
      ]}
      primary={{ label: 'Back to Home', to: '/' }}
      secondary={{ label: 'Send it home', to: '/transfers/send' }}
    />
  )
}

export function TopUpDoneScreen() {
  return (
    <FlowSuccess
      headline="Card topped up"
      amount="$200.00"
      caption={`Mastercard ••4568 · from your ${BRAND.name} balance`}
      rows={[
        { label: 'Top up', value: '$200.00' },
        { label: 'Fee', value: 'Free', emphasis: true },
        { label: 'Available', value: 'Right now' },
      ]}
      primary={{ label: 'Back to my cards', to: '/cards' }}
      secondary={{ label: 'Top up again', to: '/top-up' }}
    />
  )
}

export function AccountOpenedScreen() {
  return (
    <FlowSuccess
      headline="Your account is open"
      amount="MX$0.00"
      caption="Mexican Peso · CLABE issued in your name"
      rows={[
        { label: 'Account opening', value: 'Free', emphasis: true },
        { label: 'First deposit', value: '$50.00' },
        { label: 'CLABE', value: '646 180 1234 5678 90' },
        { label: 'Ready', value: 'Now' },
      ]}
      primary={{ label: 'See my accounts', to: '/accounts' }}
      secondary={{ label: 'Open another one', to: '/open/account' }}
      footnote="Your $50 is in the account — nothing was deducted from it."
    />
  )
}

export function CardOpenedScreen() {
  return (
    <FlowSuccess
      headline="Your card is ready"
      amount="•••• 1182"
      caption="Virtual card · add it to Apple Pay now"
      rows={[
        { label: 'Card', value: 'Virtual' },
        { label: 'Paid today', value: '$9.00' },
        { label: 'Monthly fee', value: 'Free', emphasis: true },
        { label: 'Cashback', value: '0.5% on everything' },
      ]}
      primary={{ label: 'See my cards', to: '/cards' }}
      secondary={{ label: 'How cashback works', to: '/cashback' }}
    />
  )
}

export function VaultOpenedScreen() {
  return (
    <FlowSuccess
      headline="Your Vault is open"
      amount="$2,500.00"
      caption="Earning from today, withdraw on any day"
      rows={[
        { label: 'Deposited', value: '$2,500.00' },
        { label: 'Rate today', value: '2.05% · SOFR − 1.80' },
        { label: 'Interest paid', value: 'Monthly' },
        { label: 'Opening fee', value: 'None', emphasis: true },
      ]}
      primary={{ label: 'See my investments', to: '/invest' }}
      secondary={{ label: 'Open another one', to: '/invest/vault/open' }}
      footnote="The rate moves with SOFR. It is not fixed and it is not a deposit guarantee."
    />
  )
}

export function CryptoBoughtScreen() {
  return (
    <FlowSuccess
      headline="Bought"
      amount="499.50 USDC"
      caption="Settled on Base · in your crypto wallet"
      rows={[
        { label: 'You spent', value: '$500.00' },
        { label: 'Spread · 0.10%', value: '$0.50' },
        { label: 'Trading fee', value: 'None', emphasis: true },
        { label: 'Settled', value: 'In seconds' },
      ]}
      primary={{ label: 'Back to my wallet', to: '/crypto' }}
      secondary={{ label: 'Convert to spendable', to: '/crypto/convert' }}
      footnote="Crypto is not a deposit and its value can fall to zero."
    />
  )
}

export function CryptoConvertedScreen() {
  return (
    <FlowSuccess
      headline="Converted"
      amount="$999.00"
      caption="From USDC into your US Dollar account"
      rows={[
        { label: 'Converted', value: '1,000.00 USDC' },
        { label: 'Spread · 0.10%', value: '$1.00' },
        { label: 'Conversion fee', value: 'None', emphasis: true },
        { label: 'Available', value: 'Right now' },
      ]}
      primary={{ label: 'See my accounts', to: '/accounts' }}
      secondary={{ label: 'Back to my wallet', to: '/crypto' }}
      footnote="You can spend it with your card straight away."
    />
  )
}

export function EsimBoughtScreen() {
  return (
    <FlowSuccess
      headline="Your eSIM is ready"
      amount="$14.00"
      caption="5 GB · 30 days · Mexico · Telcel"
      rows={[
        { label: 'Data', value: '5 GB · 30 days' },
        { label: 'Starts', value: 'When you land' },
        { label: 'Activation fee', value: 'None', emphasis: true },
        { label: 'Paid from', value: 'US Dollar balance' },
      ]}
      primary={{ label: 'Install it now', to: '/esim/qr' }}
      secondary={{ label: 'Back to eSIM', to: '/esim' }}
      footnote="Install it over Wi-Fi before you fly — you cannot scan the code without data."
    />
  )
}

export function HotelBookedScreen() {
  return (
    <FlowSuccess
      headline="Your stay is booked"
      amount="$558.00"
      caption="12–15 Oct · 2 guests · free cancellation until 10 October"
      rows={[
        { label: 'Room · 3 nights', value: '$558.00' },
        { label: 'Booking fee', value: 'None' },
        { label: 'FX fee', value: 'None · charged in USD' },
        { label: 'Cashback after check-out', value: '+$16.74', emphasis: true },
      ]}
      primary={{ label: 'Back to Travel', to: '/travel' }}
      secondary={{ label: 'Add a travel eSIM', to: '/esim/buy' }}
      footnote="The confirmation is in your email and in your transactions."
    />
  )
}

export function FlightBookedScreen() {
  const [params] = useSearchParams()
  const flight = findFlight(params.get('flight') ?? '') ?? FLIGHTS[0]!
  const total = money(Number(params.get('total')) || fromMajor(flight.fare + flight.taxes, 'USD').minorUnits, 'USD')
  const cashback = money(Math.round(total.minorUnits * FLIGHT_CASHBACK), 'USD')

  return (
    <FlowSuccess
      headline="You're flying"
      amount={formatMoney(total)}
      caption={`${flight.route} · ${flight.airline} · 12 Oct`}
      rows={[
        { label: 'Base fare', value: formatMoney(fromMajor(flight.fare, 'USD')) },
        { label: 'Taxes and extras', value: formatMoney(money(total.minorUnits - fromMajor(flight.fare, 'USD').minorUnits, 'USD')) },
        { label: 'Baggage', value: flight.bag },
        { label: 'Cashback', value: `+${formatMoney(cashback)}`, emphasis: true },
      ]}
      primary={{ label: 'Back to Travel', to: '/travel' }}
      secondary={{ label: 'Add a travel eSIM', to: '/esim/buy' }}
      footnote="Your boarding pass arrives 24 hours before departure."
    />
  )
}

export function WellnessBookedScreen() {
  const { id = '' } = useParams()
  const venue = findVenue(id)
  const service = venue?.services[0]
  const price = fromMajor(service?.price ?? 0, 'USD')
  const cashback = money(Math.round(price.minorUnits * WELLNESS_CASHBACK), 'USD')
  const isGym = venue?.kind === 'gym'

  return (
    <FlowSuccess
      headline={isGym ? 'Your pass is active' : 'Your appointment is set'}
      amount={formatMoney(price)}
      caption={`${venue?.name ?? 'Your booking'} · ${venue?.slots[0] ?? 'confirmed'}`}
      rows={[
        { label: service?.name ?? 'Booking', value: formatMoney(price) },
        { label: 'Booking fee', value: 'None', emphasis: true },
        { label: 'Where', value: venue?.city ?? '' },
        { label: 'Cashback', value: `+${formatMoney(cashback)}` },
      ]}
      primary={{ label: isGym ? 'Back to Gyms' : 'Back to Wellness', to: isGym ? '/gym' : '/wellness' }}
      secondary={{ label: 'See the venue', to: `/wellness/${id}` }}
      footnote="Free cancellation up to 4 hours before."
    />
  )
}

export function RealAssetInvestedScreen() {
  return (
    <FlowSuccess
      headline="You're in"
      amount="$1,000.00"
      caption="Torre Polanco · Mexico City · target yield 9.2%"
      rows={[
        { label: 'You put in', value: '$1,000.00' },
        { label: 'Target yield', value: '9.2%' },
        { label: 'Platform fee', value: '1.0% of rent' },
        { label: 'First rent', value: 'Next month', emphasis: true },
      ]}
      primary={{ label: 'See my portfolio', to: '/real-assets/portfolio' }}
      secondary={{ label: 'Browse other buildings', to: '/real-assets' }}
      footnote="Rent is a target, not a promise, and this is illiquid until completion."
    />
  )
}
